import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { db } from '../lib/db'

// Header bell on HomePage. Fetches the aggregated notifications list on
// mount (three sources merged server-side: friend requests, round invites,
// TournyFlex-authored broadcasts). Red dot indicator only shows for items
// the caller has never seen — the first time the popup is opened, all
// currently-displayed items are marked seen server-side and the dot goes
// away until a new notification arrives.
//
// The popup is centered, has an X close button, closes on backdrop click,
// and floats above the page with a soft drop shadow.
export default function NotificationBell({ playerId }) {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [unseenCount, setUnseenCount] = useState(0)
  const [open, setOpen] = useState(false)

  // Adaptive backoff: silent polls (no change in what the server returned)
  // stretch the interval 6s → 12s → 30s; any change snaps back to 6s so a
  // burst of activity stays near-real-time. Stopgap until WS-E push
  // notifications (docs/notifications.md) replace polling here.
  const lastPayloadRef = useRef('')
  const silentPollsRef = useRef(0)

  const refresh = useCallback(async () => {
    if (!playerId) return
    try {
      const { data } = await db('get_notifications', { player_id: playerId })
      if (data) {
        const sig = JSON.stringify([data.unseen_count || 0, (data.items || []).map(x => x.key)])
        if (sig === lastPayloadRef.current) {
          silentPollsRef.current += 1
        } else {
          lastPayloadRef.current = sig
          silentPollsRef.current = 0
        }
        setItems(data.items || [])
        setUnseenCount(data.unseen_count || 0)
      }
    } catch {}
  }, [playerId])

  useEffect(() => { refresh() }, [refresh])

  // Poll for new friend requests / round invites. Self-rescheduling timeout
  // (not setInterval) so each tick picks its own delay from the backoff
  // state; paused entirely while the tab is hidden, with an immediate
  // catch-up refresh when it becomes visible again.
  useEffect(() => {
    if (!playerId) return
    let timer = null
    let stopped = false
    const delayFor = () => {
      const n = silentPollsRef.current
      if (n < 3) return 6_000    // active — poll fast
      if (n < 8) return 12_000   // quiet for ~30s — ease off
      return 30_000              // idle — slow lane until something changes
    }
    const schedule = () => {
      if (stopped) return
      timer = setTimeout(async () => {
        if (document.visibilityState !== 'hidden') await refresh()
        schedule()
      }, delayFor())
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    schedule()
    return () => {
      stopped = true
      if (timer) clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [playerId, refresh])

  const openPopup = async () => {
    setOpen(true)
    // Mark every currently-displayed item seen. Server INSERT ON CONFLICT
    // DO NOTHING — safe to re-send existing keys. Optimistically clear
    // the badge locally too so the dot disappears immediately.
    const unseenKeys = items.filter(x => !x.is_seen).map(x => x.key)
    if (unseenKeys.length > 0) {
      setUnseenCount(0)
      setItems(prev => prev.map(x => ({ ...x, is_seen: true })))
      try {
        await db('mark_notifications_seen', { player_id: playerId, keys: unseenKeys })
      } catch { /* rollback would be nice but not critical */ }
    }
  }

  const closePopup = () => setOpen(false)

  const onTapItem = (item) => {
    closePopup()
    if (item.kind === 'round_invite' && item.round_payload) {
      navigate('/scorecard', { state: { ...item.round_payload, standalone: true } })
    } else if (item.kind === 'friend_request') {
      navigate(item.target_url || '/me')
    } else if (item.kind === 'app' && item.target_url) {
      navigate(item.target_url)
    }
  }

  return (
    <>
      <button
        onClick={openPopup}
        aria-label="Notifications"
        title="Notifications"
        style={{
          position: 'relative',
          width: 38, height: 38, flexShrink: 0,
          borderRadius: '50%',
          border: '1.5px solid var(--gold)',
          background: 'var(--green-deep)',
          color: 'var(--gold)',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0,
        }}
      >
        {/* Simple bell glyph */}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {unseenCount > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute', top: 2, right: 2,
              width: 10, height: 10, borderRadius: '50%',
              background: '#e53935',
              border: '2px solid var(--green-deep)',
            }}
          />
        )}
      </button>

      {open && (
        <div
          onClick={closePopup}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 420,
              background: 'var(--green-dark)',
              border: '1px solid var(--green-mid)',
              borderRadius: 14,
              boxShadow: '0 12px 40px rgba(0,0,0,0.45), 0 4px 12px rgba(0,0,0,0.25)',
              overflow: 'hidden',
              maxHeight: '80vh',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--green-mid)' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Notifications</h3>
              <button
                type="button"
                onClick={closePopup}
                aria-label="Close notifications"
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'transparent', border: '1px solid var(--green-mid)',
                  color: 'var(--cream)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.9rem', lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {items.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-300)' }}>
                  <p style={{ marginBottom: 4, fontWeight: 500 }}>No notifications yet</p>
                  <p style={{ fontSize: '0.8rem' }}>Friend requests and round invites will show here.</p>
                </div>
              ) : (
                items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => onTapItem(item)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '14px 16px',
                      background: 'transparent', border: 'none',
                      borderBottom: '1px solid var(--green-mid)',
                      cursor: 'pointer',
                      color: 'var(--cream)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>{item.title}</p>
                        {item.subtitle && (
                          <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--gray-300)' }}>{item.subtitle}</p>
                        )}
                        <p style={{ margin: '4px 0 0', fontSize: '0.68rem', color: 'var(--gray-500)', fontFamily: 'var(--font-mono)' }}>
                          {formatRelative(item.created_at)}
                        </p>
                      </div>
                      <span aria-hidden="true" style={{ color: 'var(--gray-500)', fontSize: '0.9rem', flexShrink: 0, alignSelf: 'center' }}>›</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function formatRelative(iso) {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const diffMin = Math.round((Date.now() - then) / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const hrs = Math.round(diffMin / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
